/**
 * Logs errors to console in development environments only.
 * In production, no errors are logged to console—users will never see error details in devtools.
 * Enabled when:
 * - Running locally with 'npm run dev' (import.meta.env.DEV)
 * - Deployed to AWS dev environment (VITE_BUILD_ENV === 'dev')
 */

import { ApiResponseValidationError } from '@/api/guards';
import { t } from '@/i18n';

// Check environment once at module load time for better performance
const IS_DEV = import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev';

export function logError(error: unknown): void {
  if (!IS_DEV) {
    return;
  }

  let message = 'Unknown error';
  let stack: string | undefined;

  if (
    error instanceof Error ||
    (typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof error.message === 'string')
  ) {
    message = error.message as string;
  } else if (typeof error === 'string') {
    message = error;
  } else {
    message = String(error);
  }

  if (
    (error instanceof Error && error.stack) ||
    (typeof error === 'object' &&
      error !== null &&
      'stack' in error &&
      typeof error.stack === 'string')
  ) {
    stack = error.stack as string;
  }

  console.error('[Error]', message);
  if (stack) {
    console.error(stack);
  }
}

/**
 * Returns the user-facing dialog message for known error types, or null for unknown errors.
 * This is the single source of truth for which errors show a dialog to the user.
 * Add new known error types here as the application grows.
 *
 * Handles both live instances (unhandledrejection / window.onerror) and
 * serialized plain objects (createAsyncThunk serializes thrown errors by name).
 */
export function getDialogMessageIfKnown(error: unknown): string | null {
  if (
    error instanceof ApiResponseValidationError ||
    (typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      error.name === ApiResponseValidationError.name)
  ) {
    return t('error_message');
  }
  return null;
}
